import { useEffect, useMemo, useState } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { NIcon, NTooltip } from '@innersource-nn/novoui-react';
import clsx from 'clsx';
import errorIcon from 'assets/icons/error-info.svg';

export type Option = {
    label: string;
    value?: string;
    name?: string;
};

// TODO: RECREATE THIS COMPONENT TO USE NINPUTS
interface AutocompleteTooltipProps {
    error?: string | null;
    items: Option[];
    label?: string;
    loading?: boolean;
    placeholder: string;
    tooltip?: string;
    setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    handleSelected: (selectedOption: Option) => void;
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
}

export default function AutocompleteTooltip({
    error,
    items,
    label,
    loading,
    placeholder,
    tooltip,
    setOpen,
    handleSelected,
    onChange,
    value,
    disabled,
    ...rest
}: AutocompleteTooltipProps) {
    const [selected, setSelected] = useState<Option | {}>(
        { label: value } ?? {},
    );
    const [searchTerm, setSearchTerm] = useState('');
    const [arrowDownPressed, setArrowDownPressed] = useState(false);
    const [activeOptionIndex, setActiveOptionIndex] = useState<number | null>(
        null,
    );

    useEffect(() => {
        if (!items.length) {
            setSelected({});
        } else if (!!value) {
            const foundItem = items.find(
                (item) => item.value === value || item.name === value,
            );
            if (foundItem) setSelected(foundItem);
        }
    }, [items, error]);

    const filteredItems = useMemo(() => {
        if (searchTerm === '') {
            return items;
        } else {
            return items?.filter((item) => {
                return item.label
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase());
            });
        }
    }, [searchTerm, items]);

    const handleClick = (item: Option) => {
        setSelected(item);
        handleSelected(item);
        if (onChange)
            onChange({
                target: { value: item.label },
            } as React.ChangeEvent<HTMLInputElement>);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            if (activeOptionIndex !== null) {
                handleClick(filteredItems[activeOptionIndex]);
            } else {
                if (setOpen) {
                    setOpen(true);
                }
            }
        } else if (e.key === 'ArrowDown') {
            setArrowDownPressed(true);
            setActiveOptionIndex((prevIndex) =>
                prevIndex === null
                    ? 0
                    : Math.min(prevIndex + 1, filteredItems.length - 1),
            );
        } else if (e.key === 'ArrowUp') {
            if (activeOptionIndex === 0) {
                setArrowDownPressed(false);
                setActiveOptionIndex(null);
            } else {
                setActiveOptionIndex((prevIndex) =>
                    prevIndex === null ? 0 : Math.max(prevIndex - 1, 0),
                );
            }
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (onChange) onChange(e);
        setSearchTerm(e.target.value);
    };

    return (
        <div className="relative mb-12">
            <Combobox
                value={selected}
                onChange={setSelected}
                disabled={disabled}
            >
                {label && (
                    <div className="text-true-blue text-sm flex items-center mt-8 mb-12">
                        <Combobox.Label className="input-label">
                            {label}
                        </Combobox.Label>
                        {tooltip && (
                            <NTooltip
                                content={tooltip}
                                placement="up"
                                className="ml-8"
                                iconSize={12}
                            />
                        )}
                    </div>
                )}

                <label className="input input-primary input-select">
                    <Combobox.Input
                        className="input-field input-field input-field-outline"
                        {...rest}
                        onChange={handleChange}
                        displayValue={(item: Option) => item.label}
                        placeholder={placeholder}
                        onKeyDown={handleKeyDown}
                    />
                    <Combobox.Button>
                        <NIcon
                            className="autocomplete-icon"
                            iconName="chevronDown"
                            size={16}
                        />
                    </Combobox.Button>
                </label>
                {error && (
                    <div className="flex ml-16">
                        <img src={errorIcon} alt="error information icon" />
                        <span className="text-lava-red-700 text-xs font-light">
                            {error}
                        </span>
                    </div>
                )}
                <Transition
                    enter="transition duration-100 ease-out"
                    enterFrom="transform scale-95 opacity-0"
                    enterTo="transform scale-100 opacity-100"
                    leave="transition duration-75 ease-out"
                    leaveFrom="transform scale-100 opacity-100"
                    leaveTo="transform scale-95 opacity-0"
                >
                    <Combobox.Options className="search-bar-div rounded">
                        {!loading &&
                            filteredItems.map((item, index) => (
                                <Combobox.Option
                                    key={item.name || item.label}
                                    value={item}
                                    onClick={() => handleClick(item)}
                                    onMouseEnter={() =>
                                        setActiveOptionIndex(index)
                                    }
                                >
                                    <div
                                        className={clsx(
                                            activeOptionIndex === index &&
                                                arrowDownPressed &&
                                                'bg-sea-blue-100',
                                            'dropdown-options hover:bg-sea-blue-100',
                                        )}
                                    >
                                        {item.label}
                                    </div>
                                </Combobox.Option>
                            ))}
                    </Combobox.Options>
                </Transition>
            </Combobox>
        </div>
    );
}
